import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface PropertyRecommendationWeight {
  target_kind: 'PROPERTY';
  target_id: string;
  revision: number;
  fingerprint: string | null;
  room_topology_revision: null;
  value: number;
}

export interface RecommendationWeightCommand {
  command_id: string;
  expected_revision: number;
  expected_fingerprint: string | null;
  weight: number;
}

export interface RecommendationWeightReceipt {
  target_kind: 'PROPERTY';
  target_id: string;
  result_revision: number;
  content_fingerprint: string;
  value: number;
  outcome: 'CHANGED' | 'NO_CHANGE';
  http_status: 200;
}

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const exactKeys = (value: RecordValue, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
const safeRevision = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const signed32 = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= -2_147_483_648 &&
  value <= 2_147_483_647;
const fingerprint = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f\d]{64}$/i.test(value);
const viewKeys = [
  'target_kind',
  'target_id',
  'revision',
  'fingerprint',
  'room_topology_revision',
  'value',
] as const;
const commandKeys = ['command_id', 'expected_revision', 'expected_fingerprint', 'weight'] as const;
const receiptKeys = [
  'target_kind',
  'target_id',
  'result_revision',
  'content_fingerprint',
  'value',
  'outcome',
  'http_status',
] as const;

function invalidResponse(status?: number): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    status,
    message: '추천 가중치 응답을 확인하지 못했습니다.',
  });
}

function parseView(value: unknown, propertyId: string): PropertyRecommendationWeight {
  if (
    !record(value) ||
    !exactKeys(value, viewKeys) ||
    value.target_kind !== 'PROPERTY' ||
    typeof value.target_id !== 'string' ||
    value.target_id.toLowerCase() !== propertyId.toLowerCase() ||
    !safeRevision(value.revision) ||
    (value.revision === 0
      ? value.fingerprint !== null || value.value !== 0
      : !fingerprint(value.fingerprint)) ||
    value.room_topology_revision !== null ||
    !signed32(value.value)
  )
    throw invalidResponse(200);
  return value as unknown as PropertyRecommendationWeight;
}

function validateCommand(propertyId: string, command: RecommendationWeightCommand): void {
  if (
    !isUuid(propertyId) ||
    !record(command) ||
    !exactKeys(command, commandKeys) ||
    !isUuid(command.command_id) ||
    !safeRevision(command.expected_revision) ||
    (command.expected_revision === 0
      ? command.expected_fingerprint !== null
      : !fingerprint(command.expected_fingerprint)) ||
    !signed32(command.weight)
  )
    throw invalidResponse();
}

function parseReceipt(
  value: unknown,
  propertyId: string,
  command: RecommendationWeightCommand,
): RecommendationWeightReceipt {
  const expectedResultRevision =
    command.expected_revision + (record(value) && value.outcome === 'CHANGED' ? 1 : 0);
  if (
    !record(value) ||
    !exactKeys(value, receiptKeys) ||
    value.target_kind !== 'PROPERTY' ||
    typeof value.target_id !== 'string' ||
    value.target_id.toLowerCase() !== propertyId.toLowerCase() ||
    !safeRevision(value.result_revision) ||
    value.result_revision !== expectedResultRevision ||
    !fingerprint(value.content_fingerprint) ||
    (value.outcome === 'NO_CHANGE' &&
      value.content_fingerprint !== command.expected_fingerprint) ||
    value.value !== command.weight ||
    (value.outcome !== 'CHANGED' && value.outcome !== 'NO_CHANGE') ||
    value.http_status !== 200
  )
    throw invalidResponse(200);
  return value as unknown as RecommendationWeightReceipt;
}

export async function readPropertyRecommendationWeight(
  client: ServiceClient,
  propertyId: string,
  signal?: AbortSignal,
): Promise<PropertyRecommendationWeight> {
  if (!isUuid(propertyId)) throw invalidResponse();
  const value = await client.get<unknown>(`admin/properties/${propertyId}/recommendation-weight`, {
    signal,
    expectedStatus: 200,
  });
  return parseView(value, propertyId);
}

/** Send one explicit canonical-CAS recommendation command; mutation retries stay operator controlled. */
export async function setPropertyRecommendationWeight(
  client: ServiceClient,
  propertyId: string,
  command: RecommendationWeightCommand,
): Promise<RecommendationWeightReceipt> {
  validateCommand(propertyId, command);
  const value = await client.put<unknown>(`admin/properties/${propertyId}/recommendation-weight`, {
    json: command,
  });
  return parseReceipt(value, propertyId, command);
}
